import { Trans } from '@lingui/react/macro';
import NumberField from '@/common/components/number-field/NumberField';
import { Button } from '@/common/components/ui/button';
import type { LocationPickerApi } from '@/features/geolocation/hooks/useLocationPicker';
import type { LocationValue } from '@/features/geolocation/types/location';

export interface LocationCoordinatesProps {
  value: LocationValue;
  picker: LocationPickerApi;
  disabled?: boolean;
}

/**
 * Latitude, longitude and altitude, all directly editable and two-way synced with
 * the marker. Values stay numbers here; the API boundary formats them.
 */
export function LocationCoordinates({ value, picker, disabled }: LocationCoordinatesProps) {
  const { elevation } = picker;

  const showSuspectHint = elevation.isSuspect;
  const showAutomaticAltitude = value.altitudeSource === 'manual';
  const altitudeHelperText =
    showSuspectHint || showAutomaticAltitude ? (
      <>
        {showSuspectHint && (
          <span className="block">
            <Trans>
              The service returned 0 m, which usually means it has no data here. Please check the
              altitude.
            </Trans>
          </span>
        )}
        {showAutomaticAltitude && (
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-auto px-0"
            onClick={picker.onAltitudeAuto}
          >
            <Trans>Use automatic altitude</Trans>
          </Button>
        )}
      </>
    ) : undefined;

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      <NumberField
        label={<Trans>Latitude</Trans>}
        value={value.latitude}
        onValueChange={(latitude) => picker.onCoordinatesEdit({ latitude })}
        min={-90}
        max={90}
        step={0.0000001}
        placeholder="50.0755381"
        disabled={disabled}
      />

      <NumberField
        label={<Trans>Longitude</Trans>}
        value={value.longitude}
        onValueChange={(longitude) => picker.onCoordinatesEdit({ longitude })}
        min={-180}
        max={180}
        step={0.0000001}
        placeholder="14.4378005"
        disabled={disabled}
      />

      <NumberField
        label={<Trans>Altitude (m)</Trans>}
        value={value.altitude}
        onValueChange={picker.onAltitudeEdit}
        min={-9999}
        max={9999}
        step={0.01}
        placeholder="258"
        disabled={disabled}
        helperText={altitudeHelperText}
      />
    </div>
  );
}
